// ============================================================================
// apps/web/src/App.tsx
// ----------------------------------------------------------------------------
// Root de l'app : router + AuthProvider. Logique de gating :
//   - status === 'loading' → splash
//   - status === 'unauthenticated' → /login
//   - status === 'authenticated' && must_change_password → /change-password
//   - sinon → routes normales (dashboard, settings, …)
// ============================================================================

import { lazy, Suspense, useEffect, type ReactNode, type ComponentType } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { useKiosk } from '@/lib/kiosk';

/**
 * `lazy()` durci contre les chunks périmés. Après un déploiement, le hash des
 * chunks change : un onglet resté ouvert a un `index.html` qui pointe vers des
 * fichiers disparus. Au clic sur un module, l'`import()` échoue et React met
 * l'échec EN CACHE → le module ne s'ouvre plus tant qu'on ne rafraîchit pas à
 * la main (symptôme « il faut recharger la page »). Ici, sur échec de
 * chargement, on recharge automatiquement la page UNE fois (garde anti-boucle
 * de 10 s) pour récupérer le nouvel `index.html`.
 */
function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const KEY = 'chunkReloadAt';
      const last = Number(sessionStorage.getItem(KEY) || '0');
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return new Promise<{ default: T }>(() => {}); // suspend pendant le reload
      }
      throw err;
    }),
  );
}

// Pages du premier rendu (login / home / coquille) : import statique pour
// éviter un flash de chargement au boot.
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import AppShell from '@/components/AppShell';

// Toutes les autres pages sont chargées à la demande (code-splitting par
// route) → le bundle initial reste léger, chaque module se télécharge quand
// on ouvre sa route. Le <Suspense> plus bas affiche le Splash pendant le
// chargement du chunk.
const ChangePasswordPage    = lazyWithReload(() => import('@/pages/ChangePasswordPage'));
const HelpPage              = lazyWithReload(() => import('@/pages/HelpPage'));
const DocsPage              = lazyWithReload(() => import('@/pages/DocsPage'));
const MyProfilePage         = lazyWithReload(() => import('@/pages/MyProfilePage'));
const SettingsLayout        = lazyWithReload(() => import('@/pages/settings/SettingsLayout'));
const UsersPage             = lazyWithReload(() => import('@/pages/settings/UsersPage'));
const PermissionsPage       = lazyWithReload(() => import('@/pages/settings/PermissionsPage'));
const LocationsPage         = lazyWithReload(() => import('@/pages/settings/LocationsPage'));
// LocationModulesPage (matrice PV×module) fusionnée dans LocationsPage :
// /settings/modules redirige désormais vers /settings/locations.
const HygieneSettingsPage   = lazyWithReload(() => import('@/pages/settings/HygieneSettingsPage'));
const CashSettingsPage      = lazyWithReload(() => import('@/pages/settings/CashSettingsPage'));
const AchatsSettingsPage    = lazyWithReload(() => import('@/pages/settings/AchatsSettingsPage'));
const WardrobeSettingsPage  = lazyWithReload(() => import('@/pages/settings/WardrobeSettingsPage'));
const EtiquettesSettingsPage = lazyWithReload(() => import('@/pages/settings/EtiquettesSettingsPage'));
const KioskSettingsPage     = lazyWithReload(() => import('@/pages/settings/KioskSettingsPage'));
const CuisineSettingsPage   = lazyWithReload(() => import('@/pages/settings/CuisineSettingsPage'));
const WardrobePage          = lazyWithReload(() => import('@/pages/wardrobe/WardrobePage'));
const EtiquettesPage        = lazyWithReload(() => import('@/pages/etiquettes/EtiquettesPage'));
const ProductsPage          = lazyWithReload(() => import('@/pages/products/ProductsPage'));
const DishesQuickEditPage   = lazyWithReload(() => import('@/pages/products/DishesQuickEditPage'));
const ProductionPage        = lazyWithReload(() => import('@/pages/production/ProductionPage'));
const RevenueReportingPage  = lazyWithReload(() => import('@/pages/cash/RevenueReportingPage'));
const ClyoConvertPage       = lazyWithReload(() => import('@/pages/cash/ClyoConvertPage'));
const CaisseAnalysePage     = lazyWithReload(() => import('@/pages/caisse/CaisseAnalysePage'));
const CaisseImportPage      = lazyWithReload(() => import('@/pages/caisse/CaisseImportPage'));
const PlanningPage          = lazyWithReload(() => import('@/pages/planning/PlanningPage'));
const HygienePage           = lazyWithReload(() => import('@/pages/hygiene/HygienePage'));
const HygieneTempRoundPage  = lazyWithReload(() => import('@/pages/hygiene/HygieneTempRoundPage'));
const LegalPage             = lazyWithReload(() => import('@/pages/legal/LegalPage'));
const ProceduresPage        = lazyWithReload(() => import('@/pages/procedures/ProceduresPage'));
const CashPage              = lazyWithReload(() => import('@/pages/cash/CashPage'));
const CashControlePage      = lazyWithReload(() => import('@/pages/cash/CashControlePage'));
const CashReconcilePage     = lazyWithReload(() => import('@/pages/cash/CashReconcilePage'));
const PortagePage           = lazyWithReload(() => import('@/pages/portage/PortagePage'));
const NewsletterSignupPage  = lazyWithReload(() => import('@/pages/newsletter/NewsletterSignupPage'));
const NewsletterPage        = lazyWithReload(() => import('@/pages/newsletter/NewsletterPage'));
const CandidaturesPage      = lazyWithReload(() => import('@/pages/candidatures/CandidaturesPage'));
const CandidatureApplyPage  = lazyWithReload(() => import('@/pages/candidatures/CandidatureApplyPage'));
const EventPage             = lazyWithReload(() => import('@/pages/event/EventPage'));
const SupervisionPage       = lazyWithReload(() => import('@/pages/supervision/SupervisionPage'));
const FeedbackPage          = lazyWithReload(() => import('@/pages/feedback/FeedbackPage'));
const FeedbackQuickPage     = lazyWithReload(() => import('@/pages/feedback/FeedbackQuickPage'));
const TraceCameraPage       = lazyWithReload(() => import('@/pages/feedback/TraceCameraPage'));
const LocationDashboardPage = lazyWithReload(() => import('@/pages/location/LocationDashboardPage'));
const AchatsPage            = lazyWithReload(() => import('@/pages/factures/AchatsPage'));
const FormationPage         = lazyWithReload(() => import('@/pages/formation/FormationPage'));
const FormationManagePage   = lazyWithReload(() => import('@/pages/formation/FormationManagePage'));
const BackupsPage           = lazyWithReload(() => import('@/pages/sauvegardes/BackupsPage'));

function Splash({ label = 'Chargement…' }: { label?: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

/** /scan/<slug> → saisie étiquette express du PV <slug>. */
function ScanRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/trace?pv=${slug ?? 'cuisine'}`} replace />;
}

/**
 * Accueil (route index). Confinement kiosque : une session kiosque n'a JAMAIS
 * accès à l'accueil multi-PV — sa page 1 est le tableau de bord de SON PV
 * (/pv/<slug>). Empêche de « ressortir » vers la liste de tous les PV via le
 * logo / le bouton Accueil / la flèche retour.
 */
function HomeRoute() {
  const { isKiosk, slug } = useKiosk();
  if (isKiosk && slug) return <Navigate to={`/pv/${slug}`} replace />;
  return <DashboardPage />;
}

/**
 * Écran d'erreur quand `fetchMe()` plante (Worker down, JWT invalide, CORS…).
 * Affiche le message + propose de se déconnecter pour repartir d'une session
 * propre. Sans ça, le user est piégé sur "Chargement du profil…" pour
 * l'éternité, sans pouvoir cliquer ailleurs.
 */
function ProfileLoadError({ error, onSignOut, onRetry }: {
  error: string;
  onSignOut: () => void;
  onRetry: () => void;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="max-w-md w-full space-y-4 rounded-lg border bg-card p-6 shadow">
        <div>
          <h2 className="text-lg font-semibold">Impossible de charger ton profil</h2>
          <p className="text-sm text-muted-foreground mt-1">
            La session Supabase est valide mais le Worker n'a pas répondu correctement.
          </p>
        </div>
        <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive font-mono break-all">
          {error}
        </div>
        <div className="text-xs text-muted-foreground space-y-1">
          <p><strong>Pistes à vérifier :</strong></p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Le Worker tourne-t-il ? (<code>pnpm --filter @magic/worker dev</code>)</li>
            <li>L'URL Worker est-elle bonne ? (<code>VITE_WORKER_URL</code> dans <code>.env</code>)</li>
            <li>La session Supabase n'est-elle pas expirée ? → déconnecte et reconnecte</li>
          </ul>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onRetry}
            className="flex-1 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent transition-colors"
          >
            Réessayer
          </button>
          <button
            onClick={onSignOut}
            className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
}

function Gate({ children }: { children: ReactNode }) {
  const { status, me, error, signOut, refreshMe } = useAuth();
  if (status === 'loading') return <Splash />;
  if (status === 'unauthenticated') return <Navigate to="/login" replace />;
  if (status === 'authenticated' && !me) {
    // Soit fetchMe est encore en train de tourner (pas d'erreur), soit il a
    // planté (error défini). Si planté, on affiche l'écran d'erreur avec
    // une issue de secours pour se déconnecter.
    if (error) {
      return <ProfileLoadError
        error={error}
        onSignOut={() => void signOut()}
        onRetry={() => void refreshMe()}
      />;
    }
    return <Splash label="Chargement du profil…" />;
  }
  if (me?.profile.must_change_password) return <Navigate to="/change-password" replace />;
  // NB : PAS de redirection globale des sessions kiosk ici — le hub PV renvoie
  // vers des routes gérées par le Gate (Procédures, Hygiène, Documents,
  // Planning…) dont le kiosk a légitimement besoin. Le verrou « pas d'accès aux
  // autres PV » est appliqué par PV dans LocationDashboardPage (mot de passe
  // redemandé sur un autre /pv/<slug>).
  return <>{children}</>;
}

// Après un clic sur le lien « mot de passe oublié », Supabase établit une
// session de récupération. Où que le redirect atterrisse (racine si l'URL
// /change-password n'est pas dans l'allowlist Supabase), on force le passage
// par l'écran de changement de mot de passe.
function RecoveryRedirect() {
  const { isRecovery } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (isRecovery && location.pathname !== '/change-password') {
      navigate('/change-password', { replace: true });
    }
  }, [isRecovery, location.pathname, navigate]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RecoveryRedirect />
        <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />

          {/* Inscription newsletter PUBLIQUE (client, sans login) — cible des QR
              PV et des liens site. /n/pitchoun ou /n/npa, ?pv=<slug> = PV. */}
          <Route path="/n/:brand" element={<NewsletterSignupPage />} />

          {/* Dépôt de candidature PUBLIC (sans login) — cible du site
              emploi.lepitchoun-traiteur.fr. CV envoyé au Worker public, déposé
              dans la CVthèque (source « emploi »). /postuler ou /emploi. */}
          <Route path="/postuler" element={<CandidatureApplyPage />} />
          <Route path="/emploi"   element={<CandidatureApplyPage />} />

          {/* /feedback/new accessible SANS login Magic — auto-login kiosk
              géré par le composant lui-même. Pas d'AppShell pour avoir un
              formulaire plein écran à utiliser depuis task.lepitchoun-traiteur.fr
              ou un raccourci iPhone. */}
          <Route path="/feedback/new" element={<FeedbackQuickPage />} />

          {/* Saisie traçabilité ULTRA-SIMPLIFIÉE (cuisine) : ouvre l'appareil
              photo dès l'ouverture. Même auth que /feedback/new (kiosk/PV).
              URL : /trace?pv=<slug>. */}
          <Route path="/trace" element={<TraceCameraPage />} />
          {/* Raccourci tablette : /scan = étiquette express avec choix du PV +
              mémoire (PV/mdp) sur l'appareil ; /scan/<slug> impose un PV. */}
          <Route path="/scan"       element={<Navigate to="/trace" replace />} />
          <Route path="/scan/:slug" element={<ScanRedirect />} />

          {/* Dashboard PV accessible SANS login Magic. Auto-login kiosk
              géré dans LocationDashboardPage. URL : /pv/<slug>.
              Alias courts : /cessole, /borriglione, /vieux-nice, /liberation
              redirigent vers le slug complet. */}
          <Route path="/pv/:slug"   element={<LocationDashboardPage />} />
          <Route path="/cessole"      element={<Navigate to="/pv/pitchoun-cessole"     replace />} />
          <Route path="/borriglione"  element={<Navigate to="/pv/pitchoun-borriglione" replace />} />
          <Route path="/vieux-nice"   element={<Navigate to="/pv/npa-vieux-nice"       replace />} />
          <Route path="/liberation"   element={<Navigate to="/pv/npa-liberation"       replace />} />
          <Route path="/cuisine"      element={<Navigate to="/pv/cuisine"              replace />} />
          <Route path="/bureau"       element={<Navigate to="/pv/bureau"               replace />} />

          <Route
            element={
              <Gate>
                <AppShell />
              </Gate>
            }
          >
            <Route index element={<HomeRoute />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/docs/:doc" element={<DocsPage />} />
            <Route path="/me/profile" element={<MyProfilePage />} />
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="users" replace />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="permissions" element={<PermissionsPage />} />
              <Route path="locations" element={<LocationsPage />} />
              {/* « Modules par PV » fusionné dans « Points de vente ». */}
              <Route path="modules" element={<Navigate to="/settings/locations" replace />} />
              {/* Postes et Horaires PV ont migré vers /planning. Redirects pour
                  ne pas casser les bookmarks. */}
              <Route path="positions" element={<Navigate to="/planning/positions" replace />} />
              <Route path="location-hours" element={<Navigate to="/planning/hours" replace />} />
              <Route path="hygiene" element={<HygieneSettingsPage />} />
              <Route path="cash" element={<CashSettingsPage />} />
              <Route path="achats" element={<AchatsSettingsPage />} />
              <Route path="wardrobe" element={<WardrobeSettingsPage />} />
              <Route path="etiquettes" element={<EtiquettesSettingsPage />} />
              <Route path="kiosk" element={<KioskSettingsPage />} />
              <Route path="cuisine" element={<CuisineSettingsPage />} />
            </Route>
            <Route path="/planning/*" element={<PlanningPage />} />
            <Route path="/hygiene/temperatures/round" element={<HygieneTempRoundPage />} />
            {/* Saisie hygiène fusionnée dans la Saisie rapide 4-en-1 (mode étiquette) */}
            <Route path="/hygiene/saisie" element={<Navigate to="/feedback/new?type=trace" replace />} />
            <Route path="/hygiene/*" element={<HygienePage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/procedures" element={<ProceduresPage />} />
            <Route path="/formation" element={<FormationPage />} />
            <Route path="/formation/manage" element={<FormationManagePage />} />
            <Route path="/cash" element={<CashPage />} />
            <Route path="/cash/rapprochement-auto" element={<CashReconcilePage />} />
            <Route path="/cash/controle" element={<CashControlePage />} />
            {/* Ancienne consolidation dépôts : reprise dans le contrôle caisse. */}
            <Route path="/cash/consolidation" element={<Navigate to="/cash/controle" replace />} />
            <Route path="/reporting" element={<RevenueReportingPage />} />
            <Route path="/reporting/convertisseur" element={<ClyoConvertPage />} />
            <Route path="/sauvegardes" element={<BackupsPage />} />
            <Route path="/portage" element={<PortagePage />} />
          <Route path="/caisse" element={<CaisseAnalysePage />} />
            <Route path="/caisse/import" element={<CaisseImportPage />} />
            <Route path="/supervision" element={<SupervisionPage />} />
            <Route path="/wardrobe" element={<WardrobePage />} />
            <Route path="/etiquettes" element={<EtiquettesPage />} />
            <Route path="/produits" element={<ProductsPage />} />
            <Route path="/produits/plats" element={<DishesQuickEditPage />} />
            <Route path="/achats" element={<AchatsPage />} />
            <Route path="/production" element={<ProductionPage />} />
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/candidatures" element={<CandidaturesPage />} />
            <Route path="/event" element={<EventPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
